// Sign-in: email + password → 2FA (TOTP / SMS / recovery).
// Passkey is offered as a secondary path for users who registered one.

import {
  Alert,
  Anchor,
  Box,
  Button,
  Container,
  Divider,
  Group,
  PasswordInput,
  PinInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { startAuthentication } from '@simplewebauthn/browser';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, type AuthUser, api, issueRefreshToken, setStoredToken, setStoredUser } from '../api.js';

type Step =
  | 'enter_credentials'
  | 'choose_2fa'
  | 'enter_totp'
  | 'enter_sms'
  | 'enter_recovery'
  | 'reset_request'
  | 'reset_finish'
  // Round 36: self-service email-based account recovery (no phone needed).
  // 'recover_request'   — user types email → email goes out with one-time link
  // 'recover_complete'  — user clicked the link; URL has ?recover=&email=
  // 'recover_done'      — success; shows the 10 fresh recovery codes
  | 'recover_request'
  | 'recover_complete'
  | 'recover_done';

interface ChallengeState {
  challenge_id: string;
  methods: { totp: boolean; sms: boolean };
  email: string;
  password: string;
  // Set only on first-use TOTP — admin pre-enrolled the user but they
  // haven't paired their authenticator yet. We render the QR + secret on
  // the totp-code step so they can pair on the spot instead of typing 50
  // chars of base32 from the email the admin sent them.
  totp_setup?: { qr_data_url: string; secret: string } | undefined;
}

export function LoginPage(): React.JSX.Element {
  const nav = useNavigate();
  const [step, setStep] = useState<Step>('enter_credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<ChallengeState | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [phoneHint, setPhoneHint] = useState<string | null>(null);
  // Round 36: account recovery state
  const [recoverToken, setRecoverToken] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  // Round 37 hotfix (2026-06-11): the fresh TOTP secret + QR returned by
  // /auth/recover/complete. User must scan with their authenticator BEFORE
  // continuing to sign-in — the old pairing was wiped.
  const [recoverTotpSetup, setRecoverTotpSetup] = useState<{ qr_data_url: string; secret: string } | null>(null);

  // Round 36: when the user opens /login?recover=<token>&email=<email>
  // from the email link, jump straight to the "set new password" step.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('recover');
    const e = params.get('email');
    if (t && e) {
      setRecoverToken(t);
      setEmail(e);
      setStep('recover_complete');
      // Clear the query string so a refresh doesn't re-trigger or leak
      // the token into browser history beyond what's necessary.
      try { window.history.replaceState({}, '', '/login'); } catch { /* ignore */ }
    }
  }, []);

  const onLanded = async (token: string, user: AuthUser) => {
    setStoredToken(token);
    setStoredUser(user);
    // Round 28-U: mint a refresh-token family right after login. The api
    // wrapper uses it to silently re-issue access tokens when one expires
    // mid-session; if this call fails we still proceed (user gets a 30-day
    // access token; refresh is a UX bonus, not load-bearing).
    try { await issueRefreshToken(); } catch { /* best-effort */ }
    nav('/accounts');
  };

  const onPasswordSubmit = async () => {
    if (!email.trim() || !password) return;
    setPending(true);
    setErr(null);
    try {
      const r = await api.authLoginPassword(email.trim().toLowerCase(), password);
      const ch: ChallengeState = {
        challenge_id: r.challenge_id,
        methods: r.methods,
        email: email.trim().toLowerCase(),
        password,
        totp_setup: r.totp_setup,
      };
      setChallenge(ch);
      // Skip the picker if exactly one factor is available.
      if (r.methods.totp && !r.methods.sms) setStep('enter_totp');
      else if (r.methods.sms && !r.methods.totp) {
        try {
          await api.authLogin2faSmsSend(r.challenge_id);
          setStep('enter_sms');
        } catch (sendErr) {
          setErr(humanize(sendErr, 'sms_send'));
        }
      } else setStep('choose_2fa');
    } catch (e) {
      setErr(humanize(e));
    } finally {
      setPending(false);
    }
  };

  const onTotpSubmit = async () => {
    if (!challenge || !code) return;
    setPending(true);
    setErr(null);
    try {
      const r = await api.authLogin2faTotp(challenge.challenge_id, code);
      onLanded(r.access_token, r.user);
    } catch (e) {
      setErr(humanize(e));
    } finally {
      setPending(false);
    }
  };

  const onSmsSend = async () => {
    if (!challenge) return;
    setPending(true);
    setErr(null);
    try {
      const r = await api.authLogin2faSmsSend(challenge.challenge_id);
      setPhoneHint(r.phone_hint);
      setStep('enter_sms');
    } catch (e) {
      setErr(humanize(e, 'sms_send'));
    } finally {
      setPending(false);
    }
  };

  const onSmsSubmit = async () => {
    if (!challenge || !smsCode) return;
    setPending(true);
    setErr(null);
    try {
      const r = await api.authLogin2faSmsVerify(challenge.challenge_id, smsCode);
      onLanded(r.access_token, r.user);
    } catch (e) {
      setErr(humanize(e));
    } finally {
      setPending(false);
    }
  };

  const onRecoverySubmit = async () => {
    if (!challenge || !recoveryCode) return;
    setPending(true);
    setErr(null);
    try {
      const r = await api.authLoginRecovery(challenge.email, challenge.password, recoveryCode.trim());
      onLanded(r.access_token, r.user);
    } catch (e) {
      setErr(humanize(e));
    } finally {
      setPending(false);
    }
  };

  const onPasskey = async () => {
    if (!email.trim()) {
      setErr('Enter your email first to look up your passkey.');
      return;
    }
    setPending(true);
    setErr(null);
    try {
      const begin = await api.authLoginBegin(email.trim().toLowerCase());
      const assertion = await startAuthentication({ optionsJSON: begin.options });
      const finish = await api.authLoginFinish(begin.challenge_id, assertion);
      onLanded(finish.access_token, finish.user);
    } catch (e) {
      if (e instanceof Error && /NotAllowedError|cancel/i.test(e.message)) {
        setErr('Passkey prompt was cancelled.');
      } else setErr(humanize(e));
    } finally {
      setPending(false);
    }
  };

  const onResetRequest = async () => {
    if (!email.trim()) return;
    setPending(true);
    setErr(null);
    setInfo(null);
    try {
      await api.authPasswordResetBegin(email.trim().toLowerCase());
      setInfo('If that account has a verified phone, an SMS code was sent. Valid 5 minutes.');
      setStep('reset_finish');
    } catch (e) {
      setErr(humanize(e));
    } finally {
      setPending(false);
    }
  };

  const onResetFinish = async () => {
    if (!email.trim() || !smsCode || !newPassword) return;
    setPending(true);
    setErr(null);
    try {
      await api.authPasswordResetFinish(email.trim().toLowerCase(), smsCode, newPassword);
      setInfo('Password updated. Sign in below.');
      setStep('enter_credentials');
      setPassword('');
      setSmsCode('');
      setNewPassword('');
    } catch (e) {
      setErr(humanize(e));
    } finally {
      setPending(false);
    }
  };

  // Round 36: self-service email-based account recovery.
  // The backend is anti-enumeration — it always returns 200 even for
  // unknown emails. Surfacing a uniform confirmation here matches that.
  const onRecoverBegin = async () => {
    if (!email.trim()) return;
    setPending(true);
    setErr(null);
    setInfo(null);
    try {
      await api.authRecoverBegin(email.trim().toLowerCase());
      setInfo(
        "If that email matches an account, we've sent a recovery link. Check your inbox — the link expires in 1 hour. You can close this tab.",
      );
    } catch (e) {
      setErr(humanize(e));
    } finally {
      setPending(false);
    }
  };

  const onRecoverComplete = async () => {
    if (!email.trim() || !recoverToken || !newPassword) return;
    setPending(true);
    setErr(null);
    try {
      const r = await api.authRecoverComplete(
        email.trim().toLowerCase(),
        recoverToken,
        newPassword,
      );
      setRecoveryCodes(r.recovery_codes);
      setRecoverTotpSetup(r.totp_setup);
      setStep('recover_done');
      setPassword('');
      setNewPassword('');
      setRecoverToken(null);
    } catch (e) {
      setErr(humanize(e));
    } finally {
      setPending(false);
    }
  };

  return (
    <Container size="xs" py="xl">
      <Stack>
        <Box>
          <Title order={2}>ai-outlook</Title>
          <Text c="dimmed" size="sm">
            Self-hosted bilingual email assistant — sign in to manage accounts, alerts, and drafts.
          </Text>
        </Box>

        {err && (
          <Alert color="red" title="Sign-in failed">
            {err}
          </Alert>
        )}
        {info && (
          <Alert color="blue" title="Heads up">
            {info}
          </Alert>
        )}

        {step === 'enter_credentials' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onPasswordSubmit();
            }}
          >
            <Stack>
              <TextInput
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                type="email"
                autoComplete="username webauthn"
                required
              />
              <PasswordInput
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                autoComplete="current-password"
                required
              />
              <Group justify="space-between">
                <Button type="submit" loading={pending} disabled={!email.trim() || !password}>
                  Sign in
                </Button>
                <Anchor
                  size="sm"
                  component="button"
                  type="button"
                  onClick={() => setStep('reset_request')}
                >
                  Forgot password?
                </Anchor>
              </Group>
              {/* Round 36: email-based account recovery. The "Forgot
                  password?" path above requires a verified phone, but most
                  users have none configured. This second link routes to an
                  email-only flow that ALSO wipes TOTP so users locked out
                  of their authenticator can recover without admin help. */}
              <Group justify="center">
                <Anchor
                  size="xs"
                  c="dimmed"
                  component="button"
                  type="button"
                  onClick={() => { setEmail(email); setStep('recover_request'); setErr(null); setInfo(null); }}
                >
                  Locked out completely? (Authenticator app lost or "Invalid Code")
                </Anchor>
              </Group>
              <Divider label="or" />
              <Button variant="default" onClick={onPasskey} loading={pending}>
                Sign in with passkey (Touch ID / hardware key)
              </Button>
              <Divider />
              <Stack gap={4} align="center">
                <Text size="sm">
                  BroadNet team member? <Anchor component={Link} to="/register" fw={500}>Register here</Anchor>
                </Text>
                <Text size="xs" c="dimmed">
                  Open to @broadnet.me and @broadnetme.com email addresses
                </Text>
              </Stack>
            </Stack>
          </form>
        )}

        {step === 'choose_2fa' && challenge && (
          <Stack>
            <Title order={4}>Two-factor verification</Title>
            <Text size="sm" c="dimmed">
              Pick how to verify it's you.
            </Text>
            {challenge.methods.totp && (
              <Button onClick={() => setStep('enter_totp')}>
                Authenticator app code
              </Button>
            )}
            {challenge.methods.sms && (
              <Button variant="default" onClick={onSmsSend} loading={pending}>
                Send code via SMS
              </Button>
            )}
            <Anchor
              size="sm"
              component="button"
              type="button"
              onClick={() => setStep('enter_recovery')}
            >
              Use a recovery code instead
            </Anchor>
          </Stack>
        )}

        {step === 'enter_totp' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onTotpSubmit();
            }}
          >
            <Stack>
              <Title order={4}>Authenticator code</Title>
              {challenge?.totp_setup && (
                <Alert color="blue" title="First time? Scan this once.">
                  <Stack gap="sm">
                    <Text size="sm">
                      Open Google Authenticator / 1Password / Microsoft Authenticator → "Add account"
                      → "Scan QR code" — point your phone camera at this image:
                    </Text>
                    <Box style={{ display: 'flex', justifyContent: 'center' }}>
                      {/* biome-ignore lint/a11y/useAltText: QR is decorative + the secret is shown below as a typeable fallback */}
                      <img
                        src={challenge.totp_setup.qr_data_url}
                        alt="TOTP setup QR code"
                        style={{ width: 220, height: 220, background: 'white', padding: 8, borderRadius: 8 }}
                      />
                    </Box>
                    <Text size="xs" c="dimmed">
                      Can't scan? Add manually with key:{' '}
                      <Text component="span" ff="monospace" size="xs">
                        {challenge.totp_setup.secret}
                      </Text>
                    </Text>
                    <Text size="xs" c="dimmed">
                      After scanning, your app shows a 6-digit code. Enter it below — this QR
                      disappears once you've signed in successfully.
                    </Text>
                  </Stack>
                </Alert>
              )}
              <Text size="sm" c="dimmed">
                {challenge?.totp_setup
                  ? 'Now enter the 6-digit code your app shows for "ai-outlook":'
                  : 'Open Google Authenticator / 1Password / Bitwarden — enter the 6-digit code for "ai-outlook".'}
              </Text>
              <PinInput
                length={6}
                type="number"
                inputMode="numeric"
                value={code}
                onChange={setCode}
                aria-label="totp code"
                oneTimeCode
                autoFocus
              />
              <Group>
                <Button type="submit" loading={pending} disabled={code.length !== 6}>
                  Verify
                </Button>
                {challenge?.methods.sms && (
                  <Button variant="subtle" onClick={onSmsSend} disabled={pending}>
                    Send SMS instead
                  </Button>
                )}
              </Group>
              <Anchor
                size="sm"
                component="button"
                type="button"
                onClick={() => setStep('enter_recovery')}
              >
                Use a recovery code
              </Anchor>
            </Stack>
          </form>
        )}

        {step === 'enter_sms' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onSmsSubmit();
            }}
          >
            <Stack>
              <Title order={4}>SMS code</Title>
              <Text size="sm" c="dimmed">
                We sent a 6-digit code to {phoneHint ?? 'your phone'}. Valid 5 minutes.
              </Text>
              <PinInput
                length={6}
                type="number"
                inputMode="numeric"
                value={smsCode}
                onChange={setSmsCode}
                aria-label="sms code"
                oneTimeCode
                autoFocus
              />
              <Group>
                <Button type="submit" loading={pending} disabled={smsCode.length !== 6}>
                  Verify
                </Button>
                <Button variant="subtle" onClick={onSmsSend} disabled={pending}>
                  Resend
                </Button>
                {challenge?.methods.totp && (
                  <Button variant="subtle" onClick={() => setStep('enter_totp')}>
                    Use authenticator instead
                  </Button>
                )}
              </Group>
            </Stack>
          </form>
        )}

        {step === 'enter_recovery' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onRecoverySubmit();
            }}
          >
            <Stack>
              <Title order={4}>Recovery code</Title>
              <Text size="sm" c="dimmed">
                Enter one of the codes you saved during enrollment. Each code is single-use.
              </Text>
              <TextInput
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.currentTarget.value)}
                placeholder="XXXX-XXXX-XXXX"
                autoComplete="one-time-code"
                autoFocus
              />
              <Group>
                <Button type="submit" loading={pending} disabled={!recoveryCode.trim()}>
                  Sign in
                </Button>
                <Button variant="subtle" onClick={() => setStep('choose_2fa')}>
                  Back
                </Button>
              </Group>
            </Stack>
          </form>
        )}

        {step === 'reset_request' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onResetRequest();
            }}
          >
            <Stack>
              <Title order={4}>Reset password</Title>
              <Text size="sm" c="dimmed">
                Enter your email — if there's a verified phone on file, we'll text a 6-digit code.
                Without a phone on file, ask an admin (or use the CLI <code>set-password</code>
                tool on the VPS).
              </Text>
              <TextInput
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                type="email"
                required
              />
              <Group>
                <Button type="submit" loading={pending} disabled={!email.trim()}>
                  Send reset code
                </Button>
                <Button variant="subtle" onClick={() => setStep('enter_credentials')}>
                  Back
                </Button>
              </Group>
            </Stack>
          </form>
        )}

        {step === 'reset_finish' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onResetFinish();
            }}
          >
            <Stack>
              <Title order={4}>New password</Title>
              <Text size="sm" c="dimmed">
                Enter the SMS code we just sent and your new password (12+ characters).
              </Text>
              <PinInput
                length={6}
                type="number"
                inputMode="numeric"
                value={smsCode}
                onChange={setSmsCode}
                aria-label="reset sms code"
                oneTimeCode
                autoFocus
              />
              <PasswordInput
                label="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.currentTarget.value)}
                autoComplete="new-password"
                description="Minimum 12 characters."
              />
              <Group>
                <Button
                  type="submit"
                  loading={pending}
                  disabled={smsCode.length !== 6 || newPassword.length < 12}
                >
                  Update password
                </Button>
                <Button variant="subtle" onClick={() => setStep('enter_credentials')}>
                  Cancel
                </Button>
              </Group>
            </Stack>
          </form>
        )}

        {step === 'recover_request' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onRecoverBegin();
            }}
          >
            <Stack>
              <Title order={4}>Account recovery</Title>
              <Text size="sm" c="dimmed">
                Lost your authenticator app, getting "Invalid Code" after a
                phone reset, or can't sign in at all? Enter your email and
                we'll send you a one-time recovery link. Clicking the link
                will let you set a new password AND clears your old
                two-factor setup so you can pair a fresh authenticator.
              </Text>
              <TextInput
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                type="email"
                required
                autoFocus
              />
              <Group>
                <Button type="submit" loading={pending} disabled={!email.trim()}>
                  Send recovery email
                </Button>
                <Button variant="subtle" onClick={() => setStep('enter_credentials')}>
                  Back
                </Button>
              </Group>
            </Stack>
          </form>
        )}

        {step === 'recover_complete' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onRecoverComplete();
            }}
          >
            <Stack>
              <Title order={4}>Set a new password</Title>
              <Text size="sm" c="dimmed">
                You opened the recovery link for <strong>{email}</strong>.
                Choose a new password (12+ characters). Submitting will also
                clear your existing authenticator pairing so you can set up a
                fresh one on next sign-in, revoke all active sessions, and
                generate new recovery codes.
              </Text>
              <PasswordInput
                label="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.currentTarget.value)}
                autoComplete="new-password"
                description="Minimum 12 characters."
                autoFocus
              />
              <Group>
                <Button
                  type="submit"
                  loading={pending}
                  disabled={newPassword.length < 12 || !recoverToken}
                >
                  Reset account
                </Button>
                <Button variant="subtle" onClick={() => setStep('enter_credentials')}>
                  Cancel
                </Button>
              </Group>
            </Stack>
          </form>
        )}

        {step === 'recover_done' && recoveryCodes && (
          <Stack>
            <Title order={4}>Account recovered — scan the new authenticator code</Title>
            {recoverTotpSetup && (
              <>
                <Alert color="red" title="Step 1 of 2: scan the QR code with Microsoft Authenticator">
                  Your old authenticator pairing was wiped. Open Microsoft
                  Authenticator (or Google Authenticator), tap "Add account"
                  → "Other (Google, Facebook, etc.)" → "Scan QR code" and
                  point your camera at the image below. You can also type
                  the secret manually if scanning doesn't work.
                </Alert>
                <Box ta="center" p="sm" style={{ background: '#fff', borderRadius: 8 }}>
                  <img
                    src={recoverTotpSetup.qr_data_url}
                    alt="TOTP QR code"
                    style={{ width: 200, height: 200, imageRendering: 'pixelated' }}
                  />
                </Box>
                <Text size="xs" c="dimmed" ta="center">
                  Manual setup key (if QR doesn't scan):
                </Text>
                <Box
                  p="xs"
                  ta="center"
                  style={{
                    fontFamily: 'monospace',
                    background: 'var(--mantine-color-gray-1)',
                    borderRadius: 6,
                    fontSize: 12,
                    wordBreak: 'break-all',
                  }}
                >
                  {recoverTotpSetup.secret}
                </Box>
              </>
            )}
            <Alert color="yellow" title="Step 2 of 2: save these recovery codes">
              These 10 codes replace any you had before. They will not be
              shown again. Each code works once and lets you sign in if you
              lose your authenticator. Save them in a password manager,
              print them, or email them to yourself.
            </Alert>
            <Box
              p="md"
              style={{
                fontFamily: 'monospace',
                background: 'var(--mantine-color-gray-1)',
                borderRadius: 8,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {recoveryCodes.join('\n')}
            </Box>
            <Group>
              <Button
                variant="default"
                onClick={() => {
                  try {
                    void navigator.clipboard.writeText(recoveryCodes.join('\n'));
                    setInfo('Recovery codes copied to clipboard.');
                  } catch {
                    setErr('Could not copy — please select and copy manually.');
                  }
                }}
              >
                Copy codes
              </Button>
              <Button
                onClick={() => {
                  setRecoveryCodes(null);
                  setRecoverTotpSetup(null);
                  setInfo(
                    'Password reset and authenticator paired. Sign in below — your authenticator app will show a 6-digit code.',
                  );
                  setStep('enter_credentials');
                }}
              >
                I've scanned the QR — continue to sign-in
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

function humanize(e: unknown, context?: 'sms_send'): string {
  if (e instanceof ApiError) {
    if (e.status === 401) {
      if (e.message === 'invalid_credentials') return 'Email or password is wrong.';
      if (e.message === 'account_locked') {
        const detail = e.detail as { retry_after_sec?: number } | undefined;
        const sec = detail?.retry_after_sec ?? 0;
        const mins = sec > 0 ? Math.ceil(sec / 60) : 15;
        return `Too many bad password attempts. Try again in ${mins} min, or use SMS / recovery code.`;
      }
      if (e.message === 'invalid_code') return 'That code is wrong or expired.';
      if (e.message === 'invalid_or_expired_code') return 'That reset code is wrong or expired.';
      if (e.message === 'invalid_recovery_code') return 'That recovery code is invalid or used.';
      return 'Authentication failed.';
    }
    if (e.status === 403) {
      if (e.message === 'two_factor_enrollment_required')
        return 'This account has no 2FA configured. Use the "Locked out completely?" link below to recover your account — that flow will set up a fresh authenticator pairing for you.';
      return 'Access denied.';
    }
    if (e.status === 404 && e.message === 'challenge_unknown_or_expired')
      return 'Sign-in expired — start over.';
    if (e.status === 503) {
      // 503 from anywhere except sms/send is "brain unreachable", not SMS.
      // Round 28-U: until this hint was passed, every 503 (incl. brain
      // OOM-restart, Caddy "no upstreams") rendered as SMS-specific.
      if (context === 'sms_send') return 'SMS gateway unavailable. Try the authenticator code.';
      return 'Server unavailable — please try again.';
    }
    return e.message;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}
